import { createApi, type BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { QUERIES } from './constants';
import type {
  ISearchQueryParam,
  TInternalSearchRequest,
  TInternalSearchResponse,
  TSearchRawResponse,
} from './typings';

export const searchApiService = (baseQuery: BaseQueryFn) => {
  const api = createApi({
    reducerPath: 'search-api',
    baseQuery,
    tagTypes: [QUERIES.search],
    endpoints: (builder) => ({
      searchContentAndCommunity: builder.query<TSearchRawResponse, ISearchQueryParam>({
        query: ({
          creator_id,
          search_domains,
          message_type = 'community',
          search_text,
          page,
          page_size,
        }) => ({
          url: `/public/v1/forum/search_text/${search_text}`,
          params: {
            creator_id,
            search_domains,
            message_type,
            page,
            page_size,
          },
        }),
        providesTags: [QUERIES.search],
        serializeQueryArgs: ({ endpointName, queryArgs }) => {
          const domains = Array.isArray(queryArgs?.search_domains)
            ? queryArgs.search_domains.join(',')
            : queryArgs?.search_domains || '';
          return `${endpointName}-${queryArgs?.creator_id}-${domains}-${queryArgs?.search_text}-${queryArgs?.message_type}`;
        },
        forceRefetch({ currentArg, previousArg }) {
          return (
            currentArg?.search_text !== previousArg?.search_text ||
            currentArg?.creator_id !== previousArg?.creator_id ||
            currentArg?.message_type !== previousArg?.message_type ||
            JSON.stringify(currentArg?.search_domains) !==
              JSON.stringify(previousArg?.search_domains)
          );
        },

        merge: (currentState, incomingState, { arg }) => {
          if (!arg.page || arg.page === 1 || !currentState) {
            return incomingState;
          }

          // Otherwise, merge for pagination (page > 1)
          if (currentState.community && incomingState.community) {
            currentState.community.push(...incomingState.community);
          }
          if (currentState.exclusive_content && incomingState.exclusive_content) {
            currentState.exclusive_content.push(...incomingState.exclusive_content);
          }
          currentState.total_values = incomingState.total_values;
          currentState.total_pages = incomingState.total_pages;

          return currentState;
        },
      }),
      searchInternal: builder.query<TInternalSearchResponse, TInternalSearchRequest>({
        keepUnusedDataFor: 0,
        query: (body) => ({
          url: '/public/v1/forum/search',
          method: 'POST',
          body,
        }),
      }),
    }),
  });

  return api;
};
